import PhoneInputWithCountry from 'react-phone-number-input'
import React, { forwardRef, useImperativeHandle, useRef } from 'react'
import { css } from '@linaria/core'
import { sanitizeProps, useRandomId } from '@fs/zion-frontend-friends'
import { colors } from '@fs/zion-ui'
import { InputBase } from '@fs/zion-ui/quarks-authorized-use-only'
import LocationSelect from './LocationSelect'
import supportedCallingLocations from './supportedCallingLocations'
import { mobileLabelString } from './strings'
import type { PhoneNumberProps } from './types'
import useInternalPhoneNumberProps from './useInternalPhoneNumberProps'
import useTextFieldInputProps from './useTextFieldInputProps'

// replaces .PhoneInputInput customization in the <input />
const inputCss = css`
  padding: 0 8px 1px; /* The 1px bottom padding offsets the text to line up with the grid */
  // #region TextField css
  display: block;
  font-size: 16px;
  font-family: inherit;
  flex-grow: 2;
  border: none;
  background: transparent;
  color: ${colors.gray100};
  height: 36px; /* Height is 40 when adding the borders from the container in InputBase */
  max-width: 100%;

  &:disabled {
    cursor: not-allowed;
  }

  &::placeholder {
    color: ${colors.transparent.gray30};
    opacity: 1; /* Firefox */
  }

  &:focus {
    outline: none;
  }
  // #endregion TextField css
`

// replaces .PhoneInput customization in the <div> that wraps the select and <input />
const inputAndLocationCss = css`
  /* This is done to stretch the contents of this component. */
  display: flex;
  align-items: center;
  // fill larger InputBase
  width: 100%;
`

const PhoneNumber = forwardRef<HTMLInputElement, PhoneNumberProps>(function PhoneNumber(
  {
    id,
    placeholder,
    dense,
    disabled,
    label,
    name,
    onChange: parentOnChange,
    onBlur: parentOnBlur,
    validateAtGroupLevel,
    value: rawValue,
    helpMessage,
    validMessage,
    errorMessage,
    warningValidate,
    status,
    'aria-describedby': ariaDescribedBy,
    isInForm, // eslint-disable-line @typescript-eslint/no-unused-vars -- ignored
    autoComplete = 'tel',
    onFocus,
    onLocationChange,
    onlyLocations = supportedCallingLocations,
    locations,
    firstLocations: firstLocationsBase,
    defaultLocation: defaultLocationAlpha2Or3,
    locationLabel,
    ...props
  },
  ref
): JSX.Element {
  const { defaultLocation, labels, onBlur, onChange, sortedLocations, sortedLocationsNoDivider, value } =
    useInternalPhoneNumberProps({
      onlyLocations,
      firstLocationsBase,
      locations,
      locationLabel,
      defaultLocationAlpha2Or3,
      name,
      parentOnBlur,
      parentOnChange,
      rawValue,
    })
  const phoneInputRef = useRef()
  useImperativeHandle(ref, () => phoneInputRef.current)

  const inputId = useRandomId(id)

  // Refer to InputBase.propTypes for necessary shape
  const inputBaseProps = {
    id: inputId,
    label: label || props['aria-label'] ? label : mobileLabelString(),
    dense,
    disabled,
    validMessage,
    errorMessage,
    helpMessage,
    status,
    warningValidate,
    charactersRemainingLevel: 'never',
  }
  return (
    <InputBase {...inputBaseProps}>
      <PhoneInputWithCountry
        labels={labels}
        countries={sortedLocationsNoDivider}
        // have to provide full list here otherwise react-phone-number-input applies its own sorting
        countryOptionsOrder={sortedLocations}
        addInternationalOption
        numberInputProps={{
          ...sanitizeProps(props),
          className: inputCss,
          placeholder,
          id: inputId,
          autoComplete,
          ...useTextFieldInputProps({
            validateAtGroupLevel,
            value,
            helpMessage,
            validMessage,
            errorMessage,
            warningValidate,
            ariaDescribedBy,
            inputId,
          }),
        }}
        className={inputAndLocationCss}
        countrySelectComponent={LocationSelect}
        // any props needed to pass to LocationSelect can be passed here
        // countrySelectProps={locationSelectProps}
        name={name}
        disabled={disabled}
        // pass string here always otherwise PhoneInputWithCountry can misbehave
        value={value || ''}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        defaultCountry={defaultLocation}
        onCountryChange={onLocationChange}
        initialValueFormat="national"
        ref={phoneInputRef}
      />
    </InputBase>
  )
})

export default PhoneNumber
